import React from 'react';
import { createUseStyles } from 'react-jss';
import MissionRole from '@a_team/models/dist/MissionRole';
import { WorkingHoursDisplay } from '@src/views/Mission/AboutMission/NewMissionRoleRow/Timezone';
import AllLocationsModal from '../AllLocationsModal';
import useToggle from '@src/hooks/useToggle';
import Profile from '@src/stores/Profile/Profile';
import { round } from 'lodash';

interface Props {
  currentRole?: MissionRole;
  profile?: Profile;
}

const useStyles = createUseStyles({
  roleInfoContainer: {
    width: '100%',
  },
  infoRow: {
    display: 'flex',
    marginBottom: 5,
  },
  label: {
    width: '40%',
    fontWeight: 500,
  },
  value: {
    width: '60%',
  },
  seeLocations: {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  desc: {
    marginBottom: 24,
  },
  title: {
    marginBottom: 5,
    fontSize: 20,
    fontWeight: 500,
  },
});

function RoleInfo({ currentRole, profile }: Props) {
  const [locationsModalOpen, toggleLocationsModalOpen] = useToggle();
  const styles = useStyles();

  const roleName = currentRole?.category.title;

  if (!roleName) {
    return null;
  }

  const showHourlyBudget =
    currentRole?.showRateRangeToBuilders ||
    currentRole?.budgetSettings?.showHourlyBudget;

  const shouldShowMonthlyBudget =
    currentRole?.collectMonthlyRate &&
    (currentRole?.showRateRangeToBuilders ||
      currentRole?.budgetSettings?.showMonthlyBudget);

  return (
    <div className={styles.roleInfoContainer}>
      <h4 className={styles.title}>{roleName} role description</h4>
      <div className={styles.desc}>{currentRole?.headline}</div>
      {shouldShowMonthlyBudget &&
        currentRole.builderMonthlyRateMin &&
        currentRole.builderMonthlyRateMax && (
          <div className={styles.infoRow}>
            <div className={styles.label}>Company's monthly budget:</div>
            <div className={styles.value}>
              ${round(currentRole.builderMonthlyRateMin).toLocaleString()} — $
              {round(currentRole.builderMonthlyRateMax).toLocaleString()} per
              month
            </div>
          </div>
        )}

      {showHourlyBudget &&
        currentRole.builderRateMin &&
        currentRole.builderRateMax && (
          <div className={styles.infoRow}>
            <div className={styles.label}>Company's hourly budget:</div>
            <div className={styles.value}>
              ${round(currentRole.builderRateMin).toLocaleString()} — $
              {round(currentRole.builderRateMax).toLocaleString()} per month
            </div>
          </div>
        )}

      {currentRole.availability?.weeklyHoursAvailable && (
        <div className={styles.infoRow}>
          <div className={styles.label}>Minimum availability:</div>
          <div className={styles.value}>
            {currentRole.availability?.weeklyHoursAvailable} hours per week
          </div>
        </div>
      )}
      {currentRole.workingHours?.numberOfMinutesOverlap &&
        currentRole.workingHours?.daily &&
        currentRole.workingHours?.name && (
          <div className={styles.infoRow}>
            <div className={styles.label}>Working-hours overlap:</div>
            <div className={styles.value}>
              <WorkingHoursDisplay
                numberOfMinutesOverlap={
                  currentRole.workingHours?.numberOfMinutesOverlap
                }
                roleDaily={currentRole.workingHours?.daily}
                roleTimezoneName={currentRole.workingHours?.name}
              />
            </div>
          </div>
        )}
      <div className={styles.infoRow}>
        <div className={styles.label}>Required location:</div>
        <div className={styles.value}>
          <span
            onClick={toggleLocationsModalOpen}
            className={styles.seeLocations}
          >
            See locations
          </span>
        </div>
      </div>
      {currentRole.requiredSkills && currentRole.requiredSkills?.length > 0 && (
        <div className={styles.infoRow}>
          <div className={styles.label}>Required skills:</div>
          <div className={styles.value}>
            {currentRole.requiredSkills
              ?.map((skill) => skill.talentSkillName)
              .join(', ')}
          </div>
        </div>
      )}
      {currentRole.preferredSkills && currentRole.preferredSkills?.length > 0 && (
        <div className={styles.infoRow}>
          <div className={styles.label}>Preferred skills:</div>
          <div className={styles.value}>
            {currentRole.preferredSkills
              ?.map((skill) => skill.talentSkillName)
              .join(', ')}
          </div>
        </div>
      )}

      <AllLocationsModal
        open={locationsModalOpen}
        role={currentRole}
        userCountry={[
          profile?.countryRawObject,
          !!profile?.application?.userLocationInRoleReqs,
        ]}
        onClose={toggleLocationsModalOpen}
      />
    </div>
  );
}

export default RoleInfo;
